import { withGitpodSize } from "@/assets/icons/utils";
import { keyboardHelpModalOpenAtom } from "@/atoms/ui-state";
import { AccountInfo } from "@/components/AccountInfo";
import { Avatar, AvatarInitials } from "@/components/Avatar";
import { Button } from "@/components/flexkit/Button";
import { KeyboardCombo } from "@/components/keyboard/KeyboardCombo";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/podkit/dropdown/DropDown";
import { cn } from "@/components/podkit/lib/cn";
import { USER_SETTINGS_MODAL_QUERY_PARAM } from "@/components/settings/UserSettingsModal";
import { TRANSITION_CLASSES, TRANSITION_DURATION } from "@/components/SidebarButton.variants";
import { Tooltip } from "@/components/Tooltip";
import { SwitchOrgModal } from "@/components/SwitchOrgModal";
import { TierBadge } from "@/components/tiering/TierBadge";
import { useAnimatedUnmount } from "@/hooks/use-animated-unmount";
import { useFlagSettingsSplitEnabled } from "@/hooks/use-flag";
import type { TrackLocation } from "@/hooks/use-segment";
import { getPrincipal, setPrincipal } from "@/principal";
import { useGetAccount } from "@/queries/account-queries";
import { useLogout } from "@/queries/user-queries";
import { KeyboardHelpKeyBinding } from "@/utils/platform";
import { IconArrowBoxLeft } from "central-icons/IconArrowBoxLeft";
import { IconCheckmark2Small } from "central-icons/IconCheckmark2Small";
import { IconChevronGrabberVertical } from "central-icons/IconChevronGrabberVertical";
import { IconPlusSmall } from "central-icons/IconPlusSmall";
import { IconSettingsGear2 } from "central-icons/IconSettingsGear2";
import { IconSquareArrowTopRight2 } from "central-icons/IconSquareArrowTopRight2";
import { useSetAtom } from "jotai";
import { parseAsString, useQueryState } from "nuqs";
import { useCallback, useState, type FC } from "react";
import { Link, useNavigate } from "react-router-dom";

const ChevronGrabberVertical = withGitpodSize(IconChevronGrabberVertical);
const Checkmark = withGitpodSize(IconCheckmark2Small);
const PlusSmall = withGitpodSize(IconPlusSmall);
const Logout = withGitpodSize(IconArrowBoxLeft);
const ExternalLink = withGitpodSize(IconSquareArrowTopRight2);

export type OrgSwitcherProps = {
    "data-track-location": TrackLocation;
    onOpenChange?: (open: boolean) => void;
    isLocked?: boolean;
    hideUserSettings?: boolean;
    hideKeyboardShortcuts?: boolean;
};
export const OrgSwitcherNew: FC<OrgSwitcherProps> = ({
    "data-track-location": trackLocation,
    onOpenChange,
    isLocked,
    hideUserSettings: hideUserSettingsProp,
    hideKeyboardShortcuts,
}) => {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [switchToMemberId, setSwitchToMemberId] = useState<string | undefined>();
    const { data: account, isPending: isLoadingAccount } = useGetAccount();
    const navigate = useNavigate();
    const logout = useLogout();
    const setKeyboardHelpModalOpen = useSetAtom(keyboardHelpModalOpenAtom);
    const [, setUserSettingsTab] = useQueryState(USER_SETTINGS_MODAL_QUERY_PARAM, parseAsString);
    const { value: settingsSplitEnabled } = useFlagSettingsSplitEnabled();

    // Hide account settings if explicitly requested OR if the feature flag is disabled
    const hideUserSettings = hideUserSettingsProp || !settingsSplitEnabled;

    const isExpanded = isLocked;
    const { shouldRender: shouldRenderContent } = useAnimatedUnmount({
        duration: TRANSITION_DURATION,
        isHidden: !isExpanded,
    });

    const principal = getPrincipal();
    const membership = principal ? account?.memberships?.find((m) => m.userId === principal) : undefined;
    const orgName = membership?.organizationName;
    const showJoinOrg = !!account && !account.organizationId;

    const handleSwitchOrg = useCallback(
        (memberId: string) => {
            if (!memberId || memberId === principal) {
                return;
            }
            setSwitchToMemberId(memberId);
        },
        [principal],
    );

    const confirmSwitchOrg = useCallback(() => {
        if (switchToMemberId) {
            setSwitchToMemberId(undefined);
            setPrincipal(switchToMemberId);
            navigate("/", { replace: true });
        }
    }, [switchToMemberId, navigate]);

    const cancelSwitchOrg = useCallback(() => {
        setSwitchToMemberId(undefined);
    }, []);

    const handleLogout = useCallback(
        async (e: Event) => {
            e.preventDefault();
            await logout.mutateAsync();
            navigate("/");
        },
        [logout, navigate],
    );

    const handleDropdownOpenChange = useCallback(
        (open: boolean) => {
            setIsDropdownOpen(open);
            onOpenChange?.(open);
        },
        [onOpenChange],
    );

    const handleShowKeyboardHelp = useCallback(
        (e: Event) => {
            e.preventDefault();
            setKeyboardHelpModalOpen(true);
        },
        [setKeyboardHelpModalOpen],
    );

    const handleShowUserSettings = useCallback(() => {
        setIsDropdownOpen(false);
        void setUserSettingsTab("profile");
    }, [setUserSettingsTab]);

    if (!isLoadingAccount && account && !membership) {
        return <AccountInfo />;
    }

    if (isLoadingAccount || !account || !principal) {
        return <div />;
    }

    return (
        <>
            <DropdownMenu open={isDropdownOpen} onOpenChange={handleDropdownOpenChange}>
                <DropdownMenuTrigger asChild>
                    <Button
                        variant="ghost"
                        className={cn(
                            "h-[48px] w-full",
                            "flex gap-2 border-0",
                            "p-2",
                            "bg-transparent",
                            "rounded-lg",
                            "group",
                            isExpanded
                                ? "focus-visible:!outline-none focus-visible:!ring-1 focus-visible:!ring-content-primary focus-visible:!ring-offset-0"
                                : "focus-visible:!outline-none focus-visible:!outline-0 focus-visible:!ring-0",
                            isExpanded || shouldRenderContent ? "justify-between" : "justify-center",
                            shouldRenderContent
                                ? "hover:bg-surface-hover data-[state=open]:bg-surface-hover"
                                : "hover:bg-transparent",
                        )}
                        aria-label={`Switch organization. Currently in ${orgName || "Personal workspace"}`}
                        aria-expanded={isDropdownOpen}
                        aria-haspopup="menu"
                        data-testid="org-switcher"
                    >
                        <div
                            className={cn(
                                "flex w-full min-w-0 items-center gap-2",
                                TRANSITION_CLASSES,
                                !isExpanded ? "-translate-x-2" : "translate-x-0",
                            )}
                        >
                            <div
                                className={cn(
                                    "relative flex-shrink-0 rounded-lg",
                                    !isExpanded &&
                                        "group-focus-visible:ring-1 group-focus-visible:ring-content-primary group-focus-visible:ring-offset-1",
                                )}
                                data-testid="org-switcher-icon"
                            >
                                <Avatar size={32} className="size-8 rounded-lg">
                                    <AvatarInitials name={membership?.organizationName || ""} size={32} />
                                </Avatar>
                            </div>
                            {shouldRenderContent && (
                                <>
                                    <div
                                        className={cn(
                                            "flex min-w-0 flex-1 flex-col items-start justify-start overflow-hidden",
                                            TRANSITION_CLASSES,
                                            !isExpanded ? "max-w-0 opacity-0" : "max-w-full opacity-100",
                                        )}
                                        data-testid="org-switcher-text"
                                    >
                                        <div className="min-w-0 max-w-full truncate whitespace-nowrap text-left text-base font-bold leading-tight">
                                            {orgName}
                                        </div>
                                        <div className="min-w-0 max-w-full truncate whitespace-nowrap text-left text-base font-normal leading-tight">
                                            {account?.name}
                                        </div>
                                    </div>
                                    <div
                                        className={cn(
                                            "flex flex-shrink-0 items-center overflow-hidden",
                                            TRANSITION_CLASSES,
                                            !isExpanded ? "max-w-0 opacity-0" : "max-w-full opacity-100",
                                        )}
                                        data-testid="org-switcher-chevron"
                                    >
                                        <ChevronGrabberVertical
                                            className="flex-shrink-0 text-content-primary"
                                            size="sm"
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                    align={"start"}
                    className="z-50 max-h-[min(80vh,600px)] w-full max-w-md select-none overflow-y-auto rounded-lg border border-border-light shadow-sm focus-visible:ring-0"
                    style={{
                        width: isExpanded ? "var(--radix-dropdown-menu-trigger-width)" : undefined, // 256px - reasonable width for collapsed state
                    }}
                    translate="no"
                >
                    <div className="flex items-center justify-between px-3 py-2">
                        <div className="flex flex-col">
                            <div className="text-base text-content-primary">{account.name}</div>
                            <div className="text-sm text-content-secondary">{account.email}</div>
                        </div>
                        {!hideUserSettings && (
                            <Tooltip content="User settings">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="size-8 flex-shrink-0 p-0"
                                    onClick={handleShowUserSettings}
                                    aria-label="Open user settings"
                                    data-tracking-id="org-switcher-user-settings"
                                >
                                    <IconSettingsGear2 size={16} />
                                </Button>
                            </Tooltip>
                        )}
                    </div>
                    <DropdownMenuSeparator className="-mx-3" />
                    <div className="px-3 py-1.5 text-sm text-content-secondary">Organizations</div>
                    {(account?.memberships || []).map((m) => (
                        <DropdownMenuItem
                            key={"m-" + m.userId}
                            className="justify-between px-3 hover:bg-surface-hover focus:bg-surface-hover"
                            onSelect={() => handleSwitchOrg(m.userId)}
                            aria-label={`Switch to ${m.organizationName} organization`}
                            aria-current={m.userId === principal ? "true" : undefined}
                        >
                            <div className="flex min-w-0 flex-1 items-center justify-start gap-2">
                                <Avatar size={24} className="flex-shrink-0 rounded-md">
                                    <AvatarInitials name={m.organizationName} size={24} />
                                </Avatar>
                                <span className="min-w-0 truncate text-base" title={m.organizationName}>
                                    {m.organizationName}
                                </span>
                                <TierBadge tier={m.organizationTier} className="flex-shrink-0" />
                            </div>
                            <div className="flex flex-shrink-0 items-center justify-center gap-2 p-0.5 pl-2">
                                {m.userId === principal && <Checkmark size="sm" aria-hidden="true" />}
                            </div>
                        </DropdownMenuItem>
                    ))}
                    {showJoinOrg && (
                        <DropdownMenuItem
                            className="h-9 px-3 text-base hover:bg-surface-hover focus:bg-surface-hover"
                            asChild
                        >
                            <Link
                                className="text-content-brand"
                                to="/join-organization"
                                aria-label="Join an existing organization or create a new one"
                            >
                                <div className="flex min-w-0 flex-1 items-center justify-start gap-2 truncate">
                                    <div className="flex size-6 flex-shrink-0 items-center justify-center rounded-full bg-surface-muted">
                                        <PlusSmall size="base" className="text-content-brand" />
                                    </div>
                                    <span className="text-content-brand">Join or create an organization</span>
                                </div>
                            </Link>
                        </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator className="-mx-3" />
                    <DropdownMenuItem
                        className="flex items-center justify-between px-3 text-base hover:bg-surface-hover focus:bg-surface-hover"
                        asChild
                    >
                        <a href="https://ona.com/docs" target="_blank" rel="noopener noreferrer">
                            <span>Docs</span>
                            <div className="p-0.5 pl-2">
                                <ExternalLink size="sm" />
                            </div>
                        </a>
                    </DropdownMenuItem>
                    {!hideKeyboardShortcuts && (
                        <DropdownMenuItem
                            className="flex items-center justify-between px-3 text-base hover:bg-surface-hover focus:bg-surface-hover"
                            onSelect={handleShowKeyboardHelp}
                            aria-label="Show keyboard shortcuts"
                        >
                            <span>Keyboard shortcuts</span>
                            <div className="p-0.5 pl-2">
                                <KeyboardCombo keys={KeyboardHelpKeyBinding} />
                            </div>
                        </DropdownMenuItem>
                    )}
                    <DropdownMenuItem
                        className="px-3 text-base hover:bg-surface-hover focus:bg-surface-hover"
                        onSelect={handleLogout}
                        aria-label="Log out of your account"
                    >
                        <div className="flex-1">
                            <span>Log out</span>
                        </div>
                        <div className="p-0.5 pl-2">
                            <Logout size="sm" />
                        </div>
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
            {!!switchToMemberId && (
                <SwitchOrgModal
                    onClose={cancelSwitchOrg}
                    onConfirm={confirmSwitchOrg}
                    data-track-location={trackLocation}
                />
            )}
        </>
    );
};
